// Reusable Follow-up Status dropdown backed by the Master.
// Mirrors `SourceSelect.jsx`. Cache shared across instances; admins/MD get an
// optional Quick Add button. In "filter" mode it also surfaces legacy values
// + the 5 time-based bucket options (hybrid C) when used in /leads page filter.

import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X } from "@phosphor-icons/react";
import { API } from "../../App";

let _cache = null;
const _subs = new Set();
async function fetchStatuses(force = false) {
  if (_cache && !force) return _cache;
  try {
    const r = await axios.get(`${API}/leads/follow-up-statuses`, { params: { with_usage: false, include_inactive: false } });
    _cache = r.data || [];
    _subs.forEach((cb) => cb(_cache));
    return _cache;
  } catch {
    return [];
  }
}

// Tailwind palette → background + text helpers for badges.
export const STATUS_COLOR_MAP = {
  slate:   "bg-slate-100 text-slate-700",
  blue:    "bg-blue-100 text-blue-700",
  amber:   "bg-amber-100 text-amber-800",
  emerald: "bg-emerald-100 text-emerald-700",
  indigo:  "bg-indigo-100 text-indigo-700",
  violet:  "bg-violet-100 text-violet-700",
  fuchsia: "bg-fuchsia-100 text-fuchsia-700",
  rose:    "bg-rose-100 text-rose-700",
  stone:   "bg-stone-200 text-stone-800",
  green:   "bg-green-100 text-green-700",
  red:     "bg-red-100 text-red-700",
  cyan:    "bg-cyan-100 text-cyan-700",
  teal:    "bg-teal-100 text-teal-700",
  yellow:  "bg-yellow-100 text-yellow-800",
  orange:  "bg-orange-100 text-orange-700",
  pink:    "bg-pink-100 text-pink-700",
};
export const statusBadgeCls = (color = "slate") => STATUS_COLOR_MAP[color] || STATUS_COLOR_MAP.slate;

// Time-based buckets used only in mode="filter" (preserves legacy /leads filter UX).
const TIME_BUCKETS = [
  { value: "none",                  label: "None scheduled" },
  { value: "scheduled",             label: "Scheduled" },
  { value: "overdue",               label: "Overdue" },
  { value: "due_today",             label: "Due today" },
  { value: "completed_recently",    label: "Completed in last 7d" },
];

export default function FollowupStatusSelect({
  value, onChange, user, mode = "form", className = "", testId = "followup-status-select", disabled = false,
}) {
  const [statuses, setStatuses] = useState(_cache || []);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const isFilter = mode === "filter";
  const canQuickAdd = !isFilter && ["admin", "md"].includes((user?.role || "").toLowerCase());

  useEffect(() => {
    const cb = (rows) => setStatuses(rows);
    _subs.add(cb);
    fetchStatuses();
    return () => { _subs.delete(cb); };
  }, []);

  // Always include current value if it's legacy/inactive so editing old records works.
  const optionRows = useMemo(() => {
    const active = statuses.filter((s) => s.is_active && !s.is_legacy);
    const v = (value || "").trim();
    if (!v) return active;
    const present = active.some((s) => s.slug === v || s.name === v);
    if (present) return active;
    const legacy = statuses.find((s) => s.slug === v || s.name === v);
    if (legacy) return [...active, { ...legacy, _legacy_hint: true }];
    return [...active, { status_id: "free", slug: v, name: v, _legacy_hint: true }];
  }, [statuses, value]);

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-stretch gap-1">
        <select
          data-testid={testId}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="flex-1 rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-2 text-sm outline-none disabled:bg-slate-50 disabled:text-slate-400"
        >
          <option value="">{isFilter ? "Any status" : "— Select status —"}</option>
          {isFilter && (
            <optgroup label="By due-date">
              {TIME_BUCKETS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </optgroup>
          )}
          <optgroup label="By status">
            {optionRows.map((s) => (
              <option key={s.slug} value={s.slug}>
                {s.name}{s._legacy_hint ? " (legacy)" : ""}
              </option>
            ))}
          </optgroup>
        </select>
        {canQuickAdd && (
          <button
            type="button"
            onClick={() => setQuickAddOpen(true)}
            title="Quick add new status (admin/MD)"
            className="px-2 border border-slate-300 hover:bg-slate-50 text-[#002FA7]"
            data-testid={`${testId}-quickadd`}
          ><Plus size={14} weight="bold" /></button>
        )}
      </div>
      {quickAddOpen && (
        <QuickAddStatus user={user}
          onClose={() => setQuickAddOpen(false)}
          onCreated={(newRow) => {
            setQuickAddOpen(false);
            fetchStatuses(true);
            if (newRow?.slug) onChange(newRow.slug);
            toast.success(`Added "${newRow.name}"`);
          }} />
      )}
    </div>
  );
}

const COLOR_CHOICES = ["slate","blue","amber","emerald","indigo","violet","fuchsia","rose","stone","green","red","cyan","teal","yellow","orange","pink"];

function QuickAddStatus({ user, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("slate");
  const [saving, setSaving] = useState(false);
  const submit = useCallback(async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/leads/follow-up-statuses`, {
        name, color, is_active: true,
        requester_user_id: user.user_id, requester_name: user.name, requester_role: user.role,
      });
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create status");
    } finally { setSaving(false); }
  }, [name, color, user, onCreated]);

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white shadow-2xl max-w-sm w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="followup-status-quickadd">
        <div className="flex justify-between items-center">
          <h3 className="text-base font-bold">Add Follow-up Status</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Status name *</label>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
            className="rounded-none w-full bg-white border border-slate-300 focus:border-[#002FA7] p-2 text-sm outline-none mt-1"
            data-testid="followup-status-quickadd-name" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1 block">Color</label>
          <div className="flex flex-wrap gap-1.5">
            {COLOR_CHOICES.map((c) => (
              <button key={c} type="button" onClick={() => setColor(c)}
                className={`${statusBadgeCls(c)} px-2 py-1 text-[10px] font-bold uppercase tracking-wider border-2 ${color === c ? "border-slate-900" : "border-transparent"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={saving || !name.trim()}
            className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold bg-[#002FA7] text-white hover:bg-[#00227A] disabled:bg-slate-300"
            data-testid="followup-status-quickadd-submit">
            {saving ? "Adding…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Expose for callers that need to refresh after master changes
export { fetchStatuses };
